import { useState } from 'react';

import { Glow } from '@/components/ui';
import { cn } from '@/utils/cn';

import {
  HEADLINE_FROM,
  HEADLINE_TO,
  useHeadingZoomOut,
  useScrollZoom,
  useSectionReveal,
} from './useSectionReveal';

/**
 * Only the first answer appears in the design; the rest are written from the
 * product's own behaviour rather than invented, and should be reviewed as
 * copy.
 */
const FAQS = [
  {
    question: 'Is Wonder free for students?',
    answer:
      'Yes — Wonder is completely free for students. Create a profile, get matched and apply to internships at no cost.',
  },
  {
    question: 'How does the matching work?',
    answer:
      'Profiles and internships are compared semantically rather than by keyword, then filtered by the structured requirements of the role. A recruiter always makes the final decision.',
  },
  {
    question: 'What kind of companies are on Wonder?',
    answer:
      'Mostly startups and scale-ups hiring their first interns, alongside established companies running structured internship programmes.',
  },
  {
    question: 'Do I need experience?',
    answer:
      'No. Most roles on Wonder are open to students with no professional experience — your profile, skills and coursework are enough to apply.',
  },
  {
    question: 'How fast can a startup hire?',
    answer:
      'Postings go live as soon as they are approved and matched candidates surface immediately, so teams usually move from posting to offer within a couple of weeks.',
  },
];

/**
 * Frequently asked questions.
 *
 * The panels open by animating `grid-template-rows` from `0fr` to `1fr` rather
 * than by measuring a height in JavaScript. `height: auto` is not animatable,
 * and the usual workarounds either hard-code a max-height that clips longer
 * answers or re-measure on every resize; the grid track interpolates to
 * whatever the content actually is, in CSS, for free.
 *
 * One panel at a time, so the column does not grow taller than the heading
 * beside it.
 */
export function FaqSection() {
  const [openIndex, setOpenIndex] = useState(0);
  const zoom = useScrollZoom();
  const heading = useHeadingZoomOut();

  // The rows used to slide up as a block, which is the one section on the page
  // that read as a list appearing rather than as anything arriving. They now
  // come in from the side they sit on and overshoot, so the column assembles
  // row by row -- the same treatment the stats get, at the smaller scale a
  // list can carry.
  //
  // The glows are scaled and never faded: `Glow` sets its own opacity inline,
  // and a reveal would finish by animating that to 1 -- a soft orb turning
  // into a flat disc.
  const root = useSectionReveal(
    ({ reveal }) => {
      reveal('.js-faq-glow', { scale: 0.55 }, { duration: 1.6, ease: 'power2.out' }, 0);
      reveal('.js-faq-line', HEADLINE_FROM, HEADLINE_TO);
      // One row at a time, with a gap wide enough to be counted: at 0.09s the
      // five arrived as a block. They start a beat after the heading has begun
      // its rise, so the column is seen assembling beside it.
      reveal(
        '.js-faq-item',
        { x: 44, y: 32, opacity: 0, filter: 'blur(6px)' },
        { duration: 0.9, stagger: 0.22, ease: 'power3.out' },
        0.5,
      );
      // Earlier than the shared 78%: the heading sits at the very top of the
      // section, so on the default trigger a reader had a fifth of a viewport
      // of empty FAQ on screen before it began to rise.
    },
    { start: 'top 95%' },
  );

  return (
    <section
      ref={root}
      id="faqs"
      className="wonder-screen relative w-full overflow-x-clip py-20 lg:py-28"
    >
      <div>
        <Glow className="js-faq-glow top-1/4 -left-40" size={620} opacity={0.13} />
        <Glow className="js-faq-glow -right-32 bottom-0" size={520} tone="lilac" opacity={0.1} />

        <div
          ref={zoom}
          className="wonder-container wonder-measure relative grid gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,8fr)] lg:gap-28"
        >
          <h2
            ref={heading}
            className="text-[clamp(2.5rem,3.4vw,4rem)] leading-[1.08] font-semibold tracking-[-0.03em]"
          >
            <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
              <span className="js-faq-line text-on-night block">Frequently Asked</span>
            </span>{' '}
            <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
              <span className="js-faq-line text-iris-300 block">Questions</span>
            </span>
          </h2>

          <ul>
            {FAQS.map((faq, index) => {
              const isOpen = index === openIndex;
              const panelId = `faq-panel-${index}`;
              const buttonId = `faq-button-${index}`;

              return (
                <li key={faq.question} className="js-faq-item border-night-line border-b">
                  <h3>
                    <button
                      type="button"
                      id={buttonId}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenIndex(isOpen ? -1 : index)}
                      className={cn(
                        'group flex w-full items-center justify-between gap-6 py-7 text-left transition-[padding] duration-400 lg:py-10',
                        index === 0 && 'pt-0 lg:pt-0',
                        isOpen && 'pb-4 lg:pb-5',
                      )}
                    >
                      {/* The hover fills the letters, not a box behind them: a
                        two-stop gradient -- lilac then white, each half the
                        width -- clipped to the text and twice as wide as it,
                        parked with the white half showing. Hover slides it
                        left and the lilac sweeps through the words. */}
                      <span
                        className={cn(
                          'bg-clip-text text-transparent text-[clamp(1.125rem,1.25vw,1.5rem)] leading-snug font-semibold',
                          'bg-[linear-gradient(90deg,var(--color-iris-300)_50%,var(--color-on-night)_50%)] bg-[length:200%_100%] bg-[position:100%_0]',
                          'transition-[background-position] duration-[2400ms] ease-out group-hover:bg-[position:0_0]',
                        )}
                      >
                        {faq.question}
                      </span>
                      <ToggleGlyph isOpen={isOpen} />
                    </button>
                  </h3>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className={cn(
                      'grid transition-[grid-template-rows] duration-400 ease-out',
                      isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                  >
                    <div className="overflow-hidden">
                      <p
                        className={cn(
                          'text-on-night-muted max-w-3xl pb-8 text-[clamp(1rem,1.05vw,1.25rem)] leading-normal lg:pb-10',
                          'transition-opacity duration-300',
                          isOpen ? 'opacity-100' : 'opacity-0',
                        )}
                      >
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

/**
 * The plus that becomes a minus. Two bars rather than a glyph swap, so the
 * vertical stroke can collapse into the horizontal one instead of one
 * character being replaced by another.
 */
function ToggleGlyph({ isOpen }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'bg-[#F1E8FA] relative isolate grid size-8 shrink-0 place-items-center rounded-full',
        // The rotating ring: a conic sweep just outside the circle, invisible
        // until hover, then spun by Tailwind's own `animate-spin`. Masked to
        // its outer 3px so only a rim shows: a negative z-index child still
        // paints above its own parent's background, so "behind the circle"
        // put the sweep across the whole face of it.
        'before:absolute before:-inset-[3px] before:-z-10 before:rounded-full before:opacity-0 before:transition-opacity before:duration-300 before:content-[""]',
        // Two soft lobes rather than two hard wedges: the sweep eases from
        // clear to lilac and back through translucent steps, and the mask's
        // inner edge is feathered a pixel, so the ring reads as light moving
        // round the rim instead of a painted arc.
        'before:bg-[conic-gradient(from_0deg,transparent_0deg,rgba(196,176,250,0.35)_60deg,rgba(196,176,250,0.9)_110deg,rgba(196,176,250,0.35)_160deg,transparent_200deg,rgba(196,176,250,0.35)_250deg,rgba(196,176,250,0.9)_300deg,rgba(196,176,250,0.35)_340deg,transparent_360deg)]',
        'before:[mask:radial-gradient(farthest-side,transparent_calc(100%-4.5px),#000_calc(100%-2.5px))]',
        'group-hover:before:opacity-100 group-hover:before:animate-[spin_6s_linear_infinite]',
        // Hovering the question spins the glyph a half turn and lifts it on a
        // soft lilac ring. A half turn, because a plus and a minus both look
        // the same after one -- the motion reads, the meaning does not change.
        'transition-[transform,background-color,box-shadow] duration-500 ease-out',
        'group-hover:bg-on-night group-hover:scale-110 group-hover:rotate-180 group-hover:shadow-[0_0_0_5px_rgba(196,176,250,0.25)]',
      )}
    >
      <span className="bg-[#582C88] absolute h-0.5 w-3.5 rounded-full" />
      <span
        className={cn(
          'bg-[#582C88] absolute h-3.5 w-0.5 rounded-full transition-transform duration-300 ease-out',
          isOpen && 'scale-y-0',
        )}
      />
    </span>
  );
}
